import { useState, useRef } from 'react';
import { Star, Clock, ArrowRight, ChevronLeft, ChevronRight, Sparkles, TrendingUp, BadgePercent } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { BookingConsole } from '../components/BookingConsole';
import { BookingDrawer } from '../components/BookingDrawer';
import { GalleryLightbox } from '../components/GalleryLightbox';
import { StaticMap } from '../components/StaticMap';
import { services, specialists, branches, promos, galleryImages, stats } from '../data/mockData';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';

export default function HomePage() {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [bookingData, setBookingData] = useState(null);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [galleryFilter, setGalleryFilter] = useState('Todos');
  const [appliedPromo, setAppliedPromo] = useState(null);
  const teamCarouselRef = useRef(null);

  const handleBookingComplete = (data) => {
    setBookingData(data);
    setIsDrawerOpen(true);
  };

  const handleApplyPromo = (promo) => {
    setAppliedPromo(promo);
    toast.success(`Promo "${promo.name}" aplicada. ${promo.discount}% de descuento.`);
  };

  const openLightbox = (index) => {
    setLightboxIndex(index);
    setLightboxOpen(true);
  };

  const filteredGallery = galleryFilter === 'Todos' 
    ? galleryImages 
    : galleryImages.filter(img => img.category === galleryFilter);

  const scrollTeamCarousel = (direction) => {
    if (teamCarouselRef.current) {
      const scrollAmount = direction === 'left' ? -300 : 300;
      teamCarouselRef.current.scrollBy({ left: scrollAmount, behavior: 'smooth' });
    }
  };

  return (
    <div className="min-h-screen pb-20 md:pb-0" data-testid="home-page">
      {/* Hero Section with Booking Console */}
      <section className="relative min-h-screen flex items-center justify-center pt-20 md:pt-0 px-4 sm:px-6 lg:px-8" data-testid="hero-section">
        {/* Background */}
        <div className="absolute inset-0 bg-cream">
          <div className="absolute inset-0 bg-gradient-to-br from-soft-gray/50 via-transparent to-copper/5" />
          {/* Decorative editorial stripe */}
          <div className="absolute right-0 top-0 w-1/3 h-full bg-gradient-to-l from-graphite/5 to-transparent hidden lg:block" />
        </div>

        <div className="relative z-10 w-full max-w-4xl mx-auto py-12 md:py-20">
          {/* Hero text */}
          <div className="text-center mb-10 md:mb-12 animate-fade-in-up">
            <Badge className="mb-4 bg-copper/10 text-copper-dark border-0 px-4 py-1">
              <Sparkles className="w-3 h-3 mr-1" />
              Reserva en 30 segundos
            </Badge>
            <h1 className="font-heading text-4xl md:text-6xl lg:text-7xl text-graphite mb-4 tracking-tight">
              Tu próxima<br />
              <span className="text-copper italic">experiencia</span> te espera
            </h1>
            <p className="font-body text-muted-foreground text-base md:text-lg max-w-md mx-auto">
              Reserva con los mejores especialistas. Sin llamadas, sin esperas.
            </p>
          </div>

          {/* Booking Console */}
          <div className="animate-fade-in-up stagger-2">
            <BookingConsole onComplete={handleBookingComplete} />
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-16 md:py-24 px-4 sm:px-6 lg:px-8 bg-white" data-testid="services-section">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-10 md:mb-12">
            <div>
              <h2 className="font-heading text-3xl md:text-5xl text-graphite mb-2">
                Servicios en 30s
              </h2>
              <p className="text-muted-foreground">Elige, agenda y listo</p>
            </div>
            <Link 
              to="/servicios" 
              className="text-copper hover:text-copper-dark font-medium flex items-center gap-1 mt-4 md:mt-0"
              data-testid="view-all-services"
            >
              Ver todos <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
            {services.slice(0, 6).map((service, index) => (
              <div
                key={service.id}
                className={`service-card group p-5 md:p-6 rounded-2xl border border-border/50 bg-white cursor-pointer animate-fade-in-up`}
                style={{ animationDelay: `${index * 0.1}s` }}
                data-testid={`service-card-${service.id}`}
              >
                <div className="flex gap-4">
                  <div className="w-16 h-16 md:w-20 md:h-20 rounded-xl overflow-hidden flex-shrink-0">
                    <img 
                      src={service.image} 
                      alt={service.name}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <h3 className="font-medium text-graphite group-hover:text-copper transition-colors">
                          {service.name}
                        </h3>
                        <p className="text-sm text-muted-foreground line-clamp-1 mt-1">
                          {service.description}
                        </p>
                      </div>
                      {service.popular && (
                        <Badge className="badge-limited text-xs flex-shrink-0">Popular</Badge>
                      )}
                    </div>
                    <div className="flex items-center justify-between mt-3">
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Clock className="w-4 h-4" />
                        <span>{service.duration} min</span>
                      </div>
                      <span className="font-heading text-lg text-copper">${service.price}</span>
                    </div>
                  </div>
                </div>
                {service.addOns.length > 0 && (
                  <div className="mt-4 pt-4 border-t border-border/50">
                    <p className="text-xs text-muted-foreground mb-2">Add-ons sugeridos:</p>
                    <div className="flex flex-wrap gap-1">
                      {service.addOns.slice(0, 3).map((addOnId) => (
                        <span key={addOnId} className="px-2 py-0.5 bg-soft-gray/50 rounded text-xs">
                          +{addOnId.replace(/-/g, ' ')}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Gallery Section */}
      <section className="py-16 md:py-24 px-4 sm:px-6 lg:px-8 bg-cream" data-testid="gallery-section">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-10">
            <div>
              <h2 className="font-heading text-3xl md:text-5xl text-graphite mb-2">
                Galería
              </h2>
              <p className="text-muted-foreground">Inspírate con nuestro trabajo</p>
            </div>
            <Link 
              to="/galeria" 
              className="text-copper hover:text-copper-dark font-medium flex items-center gap-1 mt-4 md:mt-0"
            >
              Ver galería completa <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          {/* Filter chips */}
          <div className="flex gap-2 mb-8 overflow-x-auto hide-scrollbar pb-2">
            {['Todos', 'Corte', 'Barba', 'Color', 'Facial'].map((filter) => (
              <button
                key={filter}
                onClick={() => setGalleryFilter(filter)}
                data-testid={`gallery-filter-${filter.toLowerCase()}`}
                className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
                  galleryFilter === filter
                    ? 'bg-graphite text-cream'
                    : 'bg-white border border-border hover:border-graphite'
                }`}
              >
                {filter}
              </button>
            ))}
          </div>

          {/* Gallery grid */}
          <div className="gallery-grid">
            {filteredGallery.slice(0, 8).map((image, index) => (
              <div
                key={image.id}
                className="gallery-item cursor-pointer"
                onClick={() => openLightbox(index)}
                data-testid={`gallery-item-${image.id}`}
              >
                <img src={image.url} alt={image.title} />
                <div className="gallery-item-overlay">
                  <div className="text-white">
                    <p className="font-medium text-sm">{image.title}</p>
                    <p className="text-white/70 text-xs">{image.specialist}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Promos Section */}
      <section className="py-16 md:py-24 px-4 sm:px-6 lg:px-8 bg-white" data-testid="promos-section">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-10">
            <div>
              <Badge className="mb-3 bg-muted-green/10 text-muted-green border-0">
                <TrendingUp className="w-3 h-3 mr-1" />
                Ofertas activas
              </Badge>
              <h2 className="font-heading text-3xl md:text-5xl text-graphite">
                Promos inteligentes
              </h2>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {promos.map((promo, index) => (
              <div
                key={promo.id}
                className={`promo-card p-6 rounded-2xl border ${
                  appliedPromo?.id === promo.id
                    ? 'border-muted-green bg-muted-green/5'
                    : 'border-border/50 bg-white'
                }`}
                data-testid={`promo-card-${promo.id}`}
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="w-12 h-12 rounded-xl bg-copper/10 flex items-center justify-center">
                    <BadgePercent className="w-6 h-6 text-copper" />
                  </div>
                  {promo.limited && (
                    <Badge className="badge-limited">
                      Quedan {promo.remaining}
                    </Badge>
                  )}
                </div>
                <h3 className="font-heading text-xl text-graphite mb-2">{promo.name}</h3>
                <p className="text-muted-foreground text-sm mb-4">{promo.description}</p>
                <p className="text-xs text-muted-foreground mb-4">{promo.conditions}</p>
                
                <div className="flex items-center justify-between pt-4 border-t border-border/50">
                  <code className="text-sm font-mono bg-soft-gray px-2 py-1 rounded">
                    {promo.code}
                  </code>
                  <Button
                    onClick={() => handleApplyPromo(promo)}
                    disabled={appliedPromo?.id === promo.id}
                    size="sm"
                    className={`rounded-full ${
                      appliedPromo?.id === promo.id
                        ? 'bg-muted-green text-cream'
                        : 'bg-graphite hover:bg-graphite/90 text-cream'
                    }`}
                    data-testid={`apply-promo-${promo.id}`}
                  >
                    {appliedPromo?.id === promo.id ? 'Aplicada' : 'Aplicar'}
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-16 md:py-24 px-4 sm:px-6 lg:px-8 bg-cream" data-testid="team-section">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-10">
            <div>
              <h2 className="font-heading text-3xl md:text-5xl text-graphite mb-2">
                Nuestro equipo
              </h2>
              <p className="text-muted-foreground">Los mejores especialistas te esperan</p>
            </div>
            <div className="hidden md:flex gap-2">
              <button
                onClick={() => scrollTeamCarousel('left')}
                className="w-10 h-10 rounded-full border border-border hover:border-graphite flex items-center justify-center transition-colors"
                data-testid="team-carousel-prev"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <button
                onClick={() => scrollTeamCarousel('right')}
                className="w-10 h-10 rounded-full border border-border hover:border-graphite flex items-center justify-center transition-colors"
                data-testid="team-carousel-next"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          </div>

          <div 
            ref={teamCarouselRef}
            className="flex gap-4 md:gap-6 overflow-x-auto hide-scrollbar pb-4"
          >
            {specialists.map((specialist) => (
              <div
                key={specialist.id}
                className="flex-shrink-0 w-64 md:w-72 group"
                data-testid={`team-card-${specialist.id}`}
              >
                <div className="relative overflow-hidden rounded-2xl aspect-[3/4]">
                  <img
                    src={specialist.image}
                    alt={specialist.name}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                  <div className="team-card-overlay absolute inset-0 flex flex-col justify-end p-5 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <div className="flex flex-wrap gap-1 mb-2">
                      {specialist.specialties.map((s) => (
                        <span key={s} className="px-2 py-0.5 bg-white/20 rounded text-white text-xs">
                          {s}
                        </span>
                      ))}
                    </div>
                    <Button 
                      size="sm" 
                      className="w-full bg-copper hover:bg-copper-dark text-cream rounded-full"
                      data-testid={`choose-specialist-${specialist.id}`}
                    >
                      Elegir
                    </Button>
                  </div>
                  
                  {/* Availability indicator */}
                  <div className="absolute top-3 right-3 w-3 h-3 bg-muted-green rounded-full ring-2 ring-white" />
                </div>
                <div className="mt-4">
                  <div className="flex items-center justify-between">
                    <h3 className="font-medium text-graphite">{specialist.name}</h3>
                    <div className="flex items-center gap-1">
                      <Star className="w-4 h-4 text-copper fill-copper" />
                      <span className="text-sm">{specialist.rating}</span>
                    </div>
                  </div>
                  <p className="text-sm text-muted-foreground">{specialist.role}</p>
                  <p className="text-xs text-muted-foreground mt-1">{specialist.reviews} reseñas</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Locations Section */}
      <section className="py-16 md:py-24 px-4 sm:px-6 lg:px-8 bg-white" data-testid="locations-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-10 md:mb-12">
            <h2 className="font-heading text-3xl md:text-5xl text-graphite mb-2">
              Encuéntranos
            </h2>
            <p className="text-muted-foreground">3 sucursales en Monterrey para tu comodidad</p>
          </div>

          <StaticMap branches={branches} />
        </div>
      </section>

      {/* Booking Drawer */}
      <BookingDrawer
        isOpen={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        bookingData={bookingData}
      />

      {/* Gallery Lightbox */}
      <GalleryLightbox
        images={filteredGallery}
        initialIndex={lightboxIndex}
        isOpen={lightboxOpen}
        onClose={() => setLightboxOpen(false)}
      />
    </div>
  );
}
